import {
  getFirstAttributeValue,
  getPrice,
  rpcCall,
} from '@scayle/storefront-nuxt'
import { readonly } from 'vue'

import useUiState from './ui/useUiState'
import { getSizeLabel } from '#layers/base/types/variant'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'

import type {
  GetBackInStockSubscriptionsData,
  SubscribeToBackInStockData,
} from '~/rpcMethods'
import type { Locale } from '~/config/locales'
import { getLocaleIsoCode } from '~/config/locales'

const isLoading = ref(false)
const showError = ref(false)
const showSuccess = ref(false)
const doiRequired = ref(true)

export default () => {
  isLoading.value = false
  showError.value = false
  showSuccess.value = false

  const route = useRoute()
  const nuxtApp = useNuxtApp()
  const currentShop = useCurrentShop()
  const { activeVariant, activeProduct, showBackInStock } = useUiState()
  const { trackBackInStockRequest } = useTrackingEvents()

  const { user } = useAgsUser()

  const email = computed(() => user.value?.email)

  const sendBackInStockRequest = async (
    user: Omit<SubscribeToBackInStockData['user'], 'businessUnit'>,
  ) => {
    isLoading.value = true
    showError.value = false
    showSuccess.value = false

    try {
      const product = activeProduct.value
      const variant = activeVariant.value
      const productImage = product
        ? getProductImage(product.images, '_5')
        : undefined
      if (!product || !variant || !productImage) {
        showError.value = true
        return
      }

      const item = {
        color: getFirstAttributeValue(product.attributes, 'color')?.label || '',
        productName:
          getFirstAttributeValue(product.attributes, 'name')?.label || '',
        size: getSizeLabel(variant),
        sku: variant.referenceKey || '',
        productUrl: `${window.location.origin}${route.fullPath}`,
        variantId: variant.id.toString(),
        imageUrl: nuxtApp.$config.public.imageBaseUrl + productImage.hash,
        price: getPrice(variant),
      }

      const businessUnit = getLocaleIsoCode(currentShop.value.locale as Locale)
      const callData: SubscribeToBackInStockData = {
        user: { ...user, businessUnit },
        item,
      }

      trackBackInStockRequest(product, variant)

      const call = rpcCall(nuxtApp, 'subscribeToBackInStock', currentShop.value)
      await call(callData)

      showSuccess.value = true
    } catch {
      showError.value = true
    } finally {
      isLoading.value = false
    }
  }

  const checkIfDoiRequired = async () => {
    if (!email.value) {
      doiRequired.value = true
      return
    }

    try {
      const businessUnit = getLocaleIsoCode(currentShop.value.locale as Locale)
      const callData: GetBackInStockSubscriptionsData = {
        email: email.value,
        businessUnit,
      }

      const call = rpcCall(
        nuxtApp,
        'getBackInStockSubscriptions',
        currentShop.value,
      )
      const response = await call(callData)

      doiRequired.value = !!response.doi
    } catch {
      doiRequired.value = true
    }
  }

  watch(email, () => checkIfDoiRequired(), { immediate: true })

  watch(activeVariant, (variant) => {
    if (!showBackInStock.value && !variant) {
      return
    }

    showBackInStock.value =
      variant &&
      variant.stock.quantity === 0 &&
      !variant.stock.isSellableWithoutStock
  })

  const resetState = () => {
    isLoading.value = false
    showError.value = false
    showSuccess.value = false
  }

  return {
    sendBackInStockRequest,
    product: activeProduct,
    variant: activeVariant,
    showBackInStock,
    doiRequired,
    isLoading,
    showError: readonly(showError),
    showSuccess,
    checkIfDoiRequired,
    resetState,
  }
}
